import {
  filterFromQuery,
  filterGames,
  filterToQuery,
  genresFor,
  getCatalogue,
  INPUT_LABEL,
  isLearnish,
  KIND_LABEL,
  label,
  type Catalogue,
  type Filter,
  type Input,
  type Kind,
} from "../catalogue.ts";
import { gameGrid } from "../components.ts";
import { clear, h, link } from "../dom.ts";
import { setTitle } from "../router.ts";
import { artFirst, HALLOWEEN_ROW, homeRows } from "../rows.ts";
import { categoryStrip, featuredStrip } from "../zones/category/index.ts";

/**
 * Genre pills a player reads: with no kind picked, genres that only repeat a developer kind ("Example", "Course")
 * stay out; picking that kind in the sheet still lists them.
 */
export function playerGenres(cat: Pick<Catalogue, "taxonomy" | "games">, kind: Kind | null | undefined): string[] {
  const all = genresFor(cat, kind);
  if (kind) return all;
  const quiet = new Set<Kind>(["example", "learn"]);
  return all.filter((g) => cat.games.some((x) => x.genre === g && !quiet.has(x.kind)));
}

/** Kind → genre → facets, plus search. The filter lives in the query string, so results can be linked. */
export async function browsePage(_p: unknown, query: URLSearchParams): Promise<HTMLElement> {
  const cat = await getCatalogue();
  let f: Filter = filterFromQuery(query);
  // wave 11: the home rows double as collections here (?row=); personal and hero rows aren't collections
  const rows = homeRows(cat).filter((r) => r.style !== "hero");
  const rowOf = (id: string | null | undefined) => rows.find((r) => r.id === id) ?? null;
  if (f.row && !rowOf(f.row)) f = { ...f, row: null };
  setTitle(rowOf(f.row)?.title ?? (f.kind ? KIND_LABEL[f.kind] : "Browse"));
  const heading = h("h1", null, "Browse");
  const rowRow = h("div", { class: "filter-row rows-row", role: "group", "aria-label": "Collections" });

  // "All games" is games and experiences; courses, examples, demos and portfolios sit under "Learn" (design review
  // 19 Oct). A search, or a kind picked in the sheet, looks at everything.
  const learnCount = cat.games.filter(isLearnish).length;
  const allOf = (x: Filter) => (x.learn ? cat.games.filter(isLearnish) : x.q?.trim() || x.kind ? cat.games : cat.games.filter((g) => !isLearnish(g)));
  const results = h("div", { class: "results" });
  const count = h("p", { class: "dim count", "aria-live": "polite" });
  const genreRow = h("div", { class: "filter-row", role: "group", "aria-label": "Genre" });
  const kindRow = h("div", { class: "filter-row tabs", role: "group", "aria-label": "Kind" });

  const update = (next: Partial<Filter>, push = false) => {
    f = { ...f, ...next };
    const qs = filterToQuery(f);
    if (location.search !== qs) history[push ? "pushState" : "replaceState"](null, "", `/browse${qs}`);
    draw();
  };

  const pill = (text: string, on: boolean, onclick: () => void) =>
    h("button", { type: "button", class: `pill${on ? " on" : ""}`, "aria-pressed": on ? "true" : "false", onclick }, text);

  const search = h("input", {
    type: "search",
    id: "q",
    placeholder: "Search games, creators, genres",
    value: f.q ?? "",
    autocomplete: "off",
    oninput: (e: Event) => update({ q: (e.target as HTMLInputElement).value }),
  });

  const facetRow = h("fieldset", { class: "filter-row facets" }, h("legend", { class: "sr-only" }, "Facets"));

  const draw = () => {
    const row = rowOf(f.row);
    heading.textContent = row ? row.title : f.learn ? "Learn" : "Browse";
    setTitle(row?.title ?? (f.kind ? KIND_LABEL[f.kind] : "Browse"));
    clear(
      rowRow,
      pill("All games", !row && !f.learn, () => update({ row: null, learn: false }, true)),
      rows.map((r) => pill(r.title, row?.id === r.id, () => update({ row: row?.id === r.id ? null : r.id, learn: false }, true))),
      learnCount ? pill("Learn", !row && !!f.learn, () => update({ row: null, learn: !f.learn }, true)) : null,
      row?.id === HALLOWEEN_ROW ? link("/haunt", { class: "pill link-pill" }, "About Open Haunt") : null,
    );
    clear(
      kindRow,
      pill("All", !f.kind, () => update({ kind: null, genre: null }, true)),
      cat.taxonomy.kinds
        .filter((k) => cat.games.some((g) => g.kind === k))
        .map((k: Kind) => pill(KIND_LABEL[k], f.kind === k, () => update({ kind: k, genre: null }, true))),
    );
    const genres = playerGenres(cat, f.kind);
    clear(
      genreRow,
      genres.length > 1 ? pill("Any genre", !f.genre, () => update({ genre: null }, true)) : null,
      genres.length > 1 ? genres.map((g) => pill(label(g), f.genre === g, () => update({ genre: f.genre === g ? null : g }, true))) : null,
    );
    const check = (text: string, on: boolean, toggle: (v: boolean) => void) =>
      h("label", { class: "check" }, h("input", { type: "checkbox", checked: on, onchange: (e: Event) => toggle((e.target as HTMLInputElement).checked) }), text);
    clear(
      facetRow,
      h("legend", { class: "sr-only" }, "Facets"),
      check("Multiplayer", !!f.multiplayer, (v) => update({ multiplayer: v })),
      (Object.keys(INPUT_LABEL) as Input[]).map((i) =>
        check(INPUT_LABEL[i], !!f.input?.includes(i), (v) => update({ input: v ? [...(f.input ?? []), i] : (f.input ?? []).filter((x) => x !== i) })),
      ),
      check("Straight into play", !!f.noPrePlay, (v) => update({ noPrePlay: v })),
    );
    // a search keeps its relevance order; otherwise games with real art lead (row collections already do)
    const base = row ? row.games : allOf(f);
    const list = filterGames(row || f.q?.trim() ? base : artFirst(base), f);
    count.textContent = `${list.length} ${list.length === 1 ? "result" : "results"}`;
    const n = (f.kind ? 1 : 0) + (f.genre ? 1 : 0) + (f.multiplayer ? 1 : 0) + (f.input?.length ?? 0) + (f.noPrePlay ? 1 : 0);
    filtersBtn.textContent = n ? `Filters · ${n}` : "Filters";
    filtersBtn.classList.toggle("on", n > 0);
    showBtn.textContent = `Show ${count.textContent}`;
    clear(results, gameGrid(list, "No matches. Try fewer filters or another word.", row?.boosted));
  };
  // kind, genre and facets live in a sheet, so games are on the first screen (wave 12): one search field, one row
  // of collections, and "Filters"
  const filtersBtn = h("button", { type: "button", class: "pill filters-btn", "aria-haspopup": "dialog", onclick: () => sheet.showModal() }, "Filters");
  const showBtn = h("button", { type: "button", class: "btn", onclick: () => sheet.close() }, "Show results");
  const sheet: HTMLDialogElement = h(
    "dialog",
    { class: "sheet", "aria-label": "Filters", onclick: (e: Event) => e.target === sheet && sheet.close() },
    h(
      "div",
      { class: "sheet-in" },
      h("div", { class: "sheet-head" }, h("h2", null, "Filters"), h("button", { type: "button", class: "pill", onclick: () => update({ kind: null, genre: null, multiplayer: false, input: [], noPrePlay: false }, true) }, "Clear")),
      h("h3", null, "Kind"),
      kindRow,
      h("h3", null, "Genre"),
      genreRow,
      h("h3", null, "Play"),
      facetRow,
      h("div", { class: "sheet-foot" }, showBtn),
    ),
  );
  draw();

  return h(
    "div",
    { class: "browse" },
    h("div", { class: "browse-head" }, heading, count),
    h(
      "div",
      { class: "browse-tools" },
      h("form", { class: "search", role: "search", onsubmit: (e: Event) => e.preventDefault() }, h("label", { for: "q", class: "sr-only" }, "Search"), search),
      filtersBtn,
    ),
    // wave 13: every category is a world; the strip leads into them (hidden while searching or filtering)
    !f.q?.trim() && !f.row && !f.learn ? featuredStrip() : null,
    !f.q?.trim() && !f.row && !f.learn ? categoryStrip() : null,
    rows.length || learnCount ? rowRow : null,
    results,
    sheet,
  );
}
